import React from "react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { Split, SplitPane } from "@/components/toolkit/Split";
import { Accordion } from "@/components/toolkit/Accordion";
import { Clickable } from "@/components/toolkit/Clickable";
import { BranchSelect } from "@/components/BranchSelect";
import { CodeSectionStore } from "./store";
import { t } from "@/i18n";

interface Props {
    store: CodeSectionStore;
    leftPane: React.ReactNode;
    rightPane: React.ReactNode;
    overlays: React.ReactNode;
    onExplorerCollapse: () => void;
}

export const CodeSectionLayout = observer(({ store, leftPane, rightPane, overlays, onExplorerCollapse }: Props) => {
    if (store.isMobile) {
        const activeEditMode = store.isInEditMode(store.activeTabPath);

        const editToggle = (
            <Clickable onClick={() => store.toggleEditMode(store.activeTabPath)}>
                <button
                    className={"code_section__edit_toggle" + (activeEditMode ? " code_section__edit_toggle--active" : "")}
                    title={activeEditMode ? t("code_edit_mode_off") : t("code_edit_mode_on")}
                >✎</button>
            </Clickable>
        );

        const collapseToggle = (
            <Clickable onClick={onExplorerCollapse}>
                <button className="code_section__collapse_btn" title={t("code_collapse_tree")}>⊟</button>
            </Clickable>
        );

        const moreActionsBtn = (
            <button
                className="code_section__edit_toggle"
                title={t("code_more_actions")}
                onClick={(e) => {
                    store.contextMenuStore.show(e.clientX, e.clientY, [
                        { title: t("code_find"), value: "find" },
                    ], (value: string) => {
                        if (value === "find") {
                            if (store.isInEditMode(store.activeTabPath)) {
                                store.toggleEditMode(store.activeTabPath);
                            }
                            store.monacoEditorStore.triggerFind();
                        }
                    });
                }}
            >⋯</button>
        );

        const editorControls = (
            <>
                {store.activeRelativePath && store.mobileAccordionIndex === 1 && (
                    <Clickable onClick={() => runInAction(() => { store.mobileAccordionIndex = 1; })}>
                        <span className="code_section__active_path">{store.activeRelativePath}</span>
                    </Clickable>
                )}
                {editToggle}
                {moreActionsBtn}
            </>
        );

        const mobileItems = [
            {
                header: t("code_pane_explorer"),
                content: leftPane,
                controls: collapseToggle,
                headerSlot: !store.isLandscape && store.mobileAccordionIndex === 0 ? <BranchSelect /> : null,
            },
            {
                header: t("code_pane_editor"),
                content: rightPane,
                controls: editorControls,
                headerClassName: activeEditMode ? "accordion__header--editing" : undefined,
            },
        ];

        return (
            <div className="code_section" data-input-component="codesection" data-input-focus="click">
                <Accordion
                    items={mobileItems}
                    openIndex={store.mobileAccordionIndex}
                    onOpenChange={(i: number) => runInAction(() => { store.mobileAccordionIndex = i; })}
                    direction={store.isLandscape ? "horizontal" : "vertical"}
                />
                {overlays}
            </div>
        );
    }

    return (
        <div className="code_section" data-input-component="codesection" data-input-focus="click">
            <Split
                split="vertical"
                sizes={store.splitSizes}
                onChange={(sizes: number[]) => store.setSplitSizes(sizes)}
            >
                <SplitPane minSize={160} pinned>{leftPane}</SplitPane>
                <SplitPane minSize={200}>{rightPane}</SplitPane>
            </Split>
            {overlays}
        </div>
    );
});
